import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Add, Refresh, Edit, Search } from '@mui/icons-material';
import {
  Link,
  Typography,
  Tooltip,
  IconButton,
  Button,
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { NumberField } from '@base-ui-components/react/number-field';
import { MaterialReactTable, useMaterialReactTable, createRow } from 'material-react-table';
import { isIP } from 'is-ip';
import {
  getDeploymentDiscoveryAction,
  getDeploymentConfigAction,
  upsertDeploymentConfigAction,
  startDeploymentDiscoveryAction,
} from '../../redux/actions/deployments';
import BuildingSelector from '../buildingselector/BuildingSelector';
import { AUTH_LEVELS } from '../../redux/constants/roles';
import { defaultTableOptions } from '../util/data-table';
import { dateTime } from '../../utils/parsing';
import { buildStagingLink } from '../staging/staging';

const END_STATES = ['FAILED', 'SUCCESS', 'No History'];

const DeploymentConfiguration = ({
  deployment,
  config,
  discoveries,
  collectorIps,
  numCollectors,
  deploymentActive,
  canDiscover,
  reloadBacnet,
  startDeploymentDiscovery,
  updateDeploymentConfig,
}) => {
  const [discoveryPort, setDiscoveryPort] = React.useState(null);
  const [buildingId, setBuildingId] = useState(deployment.buildings[0]);
  const [validationErrors, setValidationErrors] = useState({});

  const renderStatus = useCallback(
    (renderedCellValue) => {
      if (renderedCellValue === 'NEW')
        return (
          <Tooltip title="Discoveries are NEW until the collector starts performing the discovery">
            {renderedCellValue}
          </Tooltip>
        );
      if (renderedCellValue === 'SUCCESS')
        return (
          <Tooltip title="Bacnet discovery completed on the collector">
            <Link href={buildStagingLink(buildingId, 'devices', {})} color="#000000">
              {renderedCellValue}
            </Link>
          </Tooltip>
        );
      if (renderedCellValue === 'FAILED')
        return (
          <Tooltip title="Bacnet discovery failed on the collector">
            <Typography variant="button" color="error">
              {renderedCellValue}
            </Typography>
          </Tooltip>
        );
      if (renderedCellValue === 'IN_PROGRESS')
        return (
          <Tooltip title="Bacnet discovery currently being run on the collector">
            <Typography variant="button" color="grey">
              {renderedCellValue}
            </Typography>
          </Tooltip>
        );
      return (
        <Tooltip title="Unknown bacnet discovery status">
          <Typography variant="button" color="grey">
            {renderedCellValue}
          </Typography>
        </Tooltip>
      );
    },
    [buildingId]
  );

  const data = config?.bacnet
    ?.map((bc) => ({
      status: 'No History',
      buildings: deployment?.buildings?.length,
      ...bc,
      ...discoveries.filter((d) => d.port === bc.port)[0],
    }))
    .toSorted((a, b) => a.port - b.port);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'network',
        header: 'Network',
        editVariant: 'select',
        editSelectOptions: collectorIps,
        muiEditTextFieldProps: {
          required: true,
          select: true,
          error: !!validationErrors?.ipPort || !!validationErrors?.ip,
          helperText: validationErrors?.ipPort || validationErrors?.ip,
          onChange: () =>
            setValidationErrors({
              ...validationErrors,
              ipPort: null,
              ip: null,
            }),
        },
      },
      {
        accessorKey: 'port',
        header: 'Port',
        enableEditing: ({ index }) => index === -1,
        muiEditTextFieldProps: {
          required: true,
          // MRT has a bug with number inputs where the arrows change the display but do not register with MRT itself, resulting in the values being wrong. So, we hide the buttons :/
          type: 'tel',
          inputProps: {
            min: 1,
            max: 65535,
          },
          error: !!validationErrors?.ipPort,
          helperText: validationErrors?.ipPort,
          onChange: () =>
            setValidationErrors({
              ...validationErrors,
              ipPort: null,
            }),
        },
      },
      {
        accessorKey: 'foreign_address',
        header: 'Foreign Address',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.foreignAddress,
          helperText: validationErrors?.foreignAddress,
          onChange: ({ target }) =>
            setValidationErrors({
              ...validationErrors,
              foreignAddress:
                target.value === '' || isIP(target.value) ? null : 'Must be a valid IPv4 Address',
            }),
        },
      },
      {
        accessorKey: 'poll_freq_secs',
        header: 'Poll Frequency (Seconds)',
        muiEditTextFieldProps: {
          // MRT has a bug with number inputs where the arrows change the display but do not register with MRT itself, resulting in the values being wrong. So, we hide the buttons :/
          type: 'tel',
          required: true,
          inputProps: {
            min: 60,
          },
          error: !!validationErrors?.poll,
          helperText: validationErrors?.poll,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              poll: undefined,
            }),
          onChange: (event) =>
            setValidationErrors({
              ...validationErrors,
              poll: event.target.value < 60 ? 'Frequency must be at least 60' : null,
            }),
        },
      },
      {
        accessorKey: 'status',
        header: 'Latest Discovery Status',
        enableEditing: false,
        Edit: () => null,

        Cell: ({ renderedCellValue }) => renderStatus(renderedCellValue),
      },
      {
        accessorKey: 'last_status_update',
        header: 'Latest Status Time',
        enableEditing: false,
        Edit: () => null,
        accessorFn: ({ last_status_update }) => dateTime(last_status_update),
      },
      {
        accessorKey: 'creator',
        header: 'Latest Discovery Creator',
        enableEditing: false,
        Edit: () => null,
      },
    ],
    [renderStatus, collectorIps, validationErrors, setValidationErrors]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    initialState: {},
    columns,
    data,
    enableTopToolbar: true,
    enableBottomToolbar: true,

    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,

    editDisplayMode: 'row',
    enableEditing: canDiscover,
    enableRowActions: canDiscover,
    getRowId: (row) => row.port,
    positionActionsColumn: 'last',

    createDisplayMode: 'modal',
    onCreatingRowSave: ({ table, values }) => {
      const { network, port } = values;
      const portNumber = Number.parseInt(port, 10);
      if (!network) {
        setValidationErrors({
          ...validationErrors,
          ip: `network must be set`,
        });
      } else if (config.bacnet.some((bc) => bc.network === network && bc.port === portNumber)) {
        setValidationErrors({
          ...validationErrors,
          ipPort: `${network}:${portNumber} already present`,
        });
      } else {
        updateDeploymentConfig(values).finally(() => table.setCreatingRow(null));
      }
    },
    onCreatingRowCancel: () => {
      // clear any validation errors
    },

    renderToolbarInternalActions: ({ table }) => (
      <>
        <Tooltip
          title={
            collectorIps.length === 0 ? 'Cannot configure bacnet until collector comes online' : ''
          }
        >
          <span aria-label={undefined}>
            <IconButton
              variant="contained"
              onClick={() =>
                table.setCreatingRow(
                  createRow(table, { network: collectorIps[0], poll_freq_secs: 60, port: 47808 })
                )
              }
              disabled={collectorIps.length === 0}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton variant="contained" onClick={reloadBacnet}>
          <Refresh />
        </IconButton>
      </>
    ),
    onEditingRowSave: ({ table }) => {
      // values should contain the right value, but for some reason it doesn't, so instead we'll pull the edit rows out
      // eslint-disable-next-line no-underscore-dangle
      updateDeploymentConfig({ ...table.getState().editingRow._valuesCache }).finally(() =>
        table.setEditingRow(null)
      );
    },
    renderRowActions: ({ table, row }) => {
      let disabled = false;
      let message = `Start discovery on bacnet port ${row.original.port}`;
      if (!deploymentActive) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment not active`;
      } else if (row.original.buildings === 0) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has no buildings`;
      } else if (numCollectors === 0) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has no active collectors`;
      } else if (numCollectors > 1) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has multiple active collectors`;
      } else if (!END_STATES.includes(row.original.status)) {
        disabled = true;
        message = `Unable to run discovery for deployment: discovery already in progress`;
      }
      return (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Tooltip title="Edit">
            <IconButton onClick={() => table.setEditingRow(row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip tag="discover" title={message} arrow>
            {
              // disabled tooltip hack https://github.com/mui/material-ui/issues/33182#issuecomment-2823321971
            }
            <span aria-label={undefined}>
              <IconButton onClick={() => setDiscoveryPort(row.original.port)} disabled={disabled}>
                <Search />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  });

  function handleSubmit(event) {
    event.preventDefault();
    startDeploymentDiscovery(
      buildingId,
      discoveryPort,
      parseInt(event.currentTarget.elements.min.value, 10),
      parseInt(event.currentTarget.elements.max.value, 10)
    );
    setDiscoveryPort(null);
  }

  return (
    <>
      <Dialog open={discoveryPort != null} onClose={() => setDiscoveryPort(null)}>
        <DialogTitle>
          Start new discovery for {deployment.name} for bacnet port {discoveryPort}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            When discovery is running there will be a period of downtime for real time data
            collection. This will create a gap in the historical record for the duration of the
            discovery process., <br />
            <br />
            Please update device id range if you wish to target discovery (the default range is
            [1-4194302]):
          </DialogContentText>
          <form onSubmit={handleSubmit} id="discovery-form">
            <DialogContentText>Minimum Device Id:</DialogContentText>
            <NumberField.Root id="min" defaultValue={1} min={1} format={{ useGrouping: false }}>
              <NumberField.Input />
            </NumberField.Root>
            <DialogContentText>Maximum Device Id:</DialogContentText>
            <NumberField.Root
              id="max"
              defaultValue={4194302}
              max={4194302}
              format={{ useGrouping: false }}
            >
              <NumberField.Input />
            </NumberField.Root>
            {deployment?.buildings?.length !== 1 ? (
              <>
                <DialogContentText>Select a building for new devices</DialogContentText>
                <BuildingSelector
                  id="bid"
                  selectedBuildingId={buildingId}
                  filter={(b) => deployment.buildings.indexOf(b.id) !== -1}
                  disabled={deployment?.buildings?.length === 1}
                  handleSelection={({ target }) => setBuildingId(target.value)}
                />
              </>
            ) : null}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscoveryPort(null)}>Cancel</Button>
          <Button type="submit" form="discovery-form">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <MaterialReactTable table={mrt} />
    </>
  );
};

function mapStateToProps(state, { deployment }) {
  const {
    entities: {
      deployments: { configs, discovery },
    },
    user: { userAuthLevel },
  } = state;
  const activeCollectors = configs[deployment.site]?.collectors?.filter((c) => c.active) ?? [];
  return {
    config: configs[deployment.site]?.configs ?? {},
    collectorIps: activeCollectors.flatMap((c) => c.device_ips),
    // gate behind superadmin utnil ready to release
    canDiscover: userAuthLevel <= AUTH_LEVELS.ORG_ADMIN,
    discoveries: discovery[deployment.site] ?? [],
    numCollectors: activeCollectors?.length ?? 0,
    deploymentActive: deployment.active,
  };
}

function mapDispatchToProps(dispatch, { deployment }) {
  return {
    reloadBacnet: () =>
      dispatch(getDeploymentDiscoveryAction(deployment.site)).then(
        dispatch(getDeploymentConfigAction(deployment.site, true))
      ),
    startDeploymentDiscovery: (buildingId, port, min, max) =>
      dispatch(startDeploymentDiscoveryAction(deployment.site, buildingId, port, min, max)),
    updateDeploymentConfig: ({ port, network, poll_freq_secs, foreign_address }) =>
      dispatch(
        upsertDeploymentConfigAction(deployment.site, {
          port,
          network,
          poll_freq_secs,
          foreign_address,
        })
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentConfiguration);
